import { RoleClient } from "@formant/core/client/RoleClient";
import { defined } from "@formant/core/common/defined";
import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IRole } from "@formant/core/model/IRole";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IRoleStore } from "./IRoleStore";
import { StoreCache } from "./StoreCache";

export class RoleStore implements IRoleStore {
    private listCache = new StoreCache<void, IRole[]>();

    constructor(
        private roleClient: RoleClient,
        private authStore: IAuthStore,
    ) {}

    list(): IRole[] | undefined {
        return this.listCache.get(
            undefined,
            async () => await this.fetchList(),
        );
    }

    async fetchList(): Promise<DefinedEntity<IRole>[]> {
        // core >= 1.49.6 returns DefinedBaseEntity<IRole>[]; widen so the
        // user's own DefinedEntity<IRole> can be pushed below
        const results: DefinedEntity<IRole>[] = await this.roleClient.list(
            await this.authStore.token(),
        );
        // if a user does not have the scope for any roles
        // add their role
        if (!results.length && this.authStore.userRole) {
            results.push(defined(this.authStore.userRole));
        }
        this.listCache.set(undefined, results);
        return results;
    }

    getByName(roleName: string): IRole | undefined {
        return this.list()?.find(_ => _.name === roleName);
    }

    async update(id: Uuid, role: Partial<IRole>) {
        const updated = await this.roleClient.update(
            await this.authStore.token(),
            id,
            role,
        );
        this.listCache.clear();
        return updated;
    }

    async save(role: IRole) {
        let saved: IRole | undefined;
        if (role.id === undefined) {
            saved = await this.create(role);
        } else {
            saved = await this.update(role.id, role);
        }
        return saved;
    }

    async create(role: IRole): Promise<IRole> {
        const created = await this.roleClient.create(
            await this.authStore.token(),
            role,
        );
        this.listCache.clear();
        return created;
    }

    get(id: Uuid): IRole | undefined {
        return this.list()?.find(_ => _.id === id);
    }

    async delete(id: Uuid): Promise<void> {
        await this.roleClient.delete(await this.authStore.token(), id);
        this.listCache.clear();
    }
}
